import React, { useState, useEffect } from 'react'
import { RefreshCw, Plus, Edit, Eye, Trash2, Filter, Search, ChevronDown } from 'lucide-react'
import { toast } from 'react-hot-toast'
import employeeAllowanceService from '../../../services/employeeAllowanceService'
import branchService from '../../../services/branchService'
import { jobService } from '../../../services/jobService'
import CreateEmployeeAllowanceModal from './CreateEmployeeAllowanceModal'
import EditEmployeeAllowanceModal from './EditEmployeeAllowanceModal'
import ViewEmployeeAllowanceModal from './ViewEmployeeAllowanceModal'

const EmployeeAllowances = () => {
  const [allowances, setAllowances] = useState([])
  const [branches, setBranches] = useState([])
  const [jobs, setJobs] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [filters, setFilters] = useState({
    branch: '',
    job: '',
    search: ''
  })

  const [modals, setModals] = useState({
    create: false,
    edit: false,
    view: false,
    delete: false
  })
  const [selectedAllowance, setSelectedAllowance] = useState(null)

  // Fetch allowances with enhanced data
  const fetchAllowances = React.useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await employeeAllowanceService.getAllEmployeeAllowances()
      if (response.success) {
        const allowancesData = response.data.allowances || []
        
        // Enhance allowances with branch and job names
        const enhancedAllowances = allowancesData.map(allowance => {
          const branch = branches.find(b => b._id === allowance.employee?.branch)
          const job = jobs.find(j => j._id === allowance.employee?.job)
          
          return {
            ...allowance,
            employee: {
              ...allowance.employee,
              branchName: branch?.name || 'N/A',
              jobTitle: job?.name || job?.title || 'N/A'
            }
          }
        })
        
        setAllowances(enhancedAllowances)
      } else {
        toast.error(response.message || 'Failed to fetch employee allowances')
      }
    } catch (error) {
      console.error('Error fetching allowances:', error)
      toast.error('Failed to fetch employee allowances')
    } finally {
      setIsLoading(false)
    }
  }, [branches, jobs])

  // Fetch data on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      // Fetch branches and jobs first
      try {
        const [branchResponse, jobResponse] = await Promise.all([
          branchService.getAllBranches(),
          jobService.getAllJobs()
        ])
        
        console.log('Branch response:', branchResponse) // Debug log
        console.log('Job response:', jobResponse) // Debug log
        
        if (branchResponse.success) {
          const branchesData = branchResponse.data.branches || branchResponse.data || []
          setBranches(branchesData)
        }
        if (jobResponse.success) {
          const jobsData = jobResponse.data.jobs || []
          setJobs(jobsData)
        }
      } catch (error) {
        console.error('Error fetching initial data:', error)
      }
    }
    
    fetchInitialData()
  }, [])

  // Fetch allowances when branches and jobs are loaded
  useEffect(() => {
    // Always try to fetch allowances after initial data is attempted
    const timer = setTimeout(() => {
      fetchAllowances()
    }, 1000) // Give time for branches and jobs to load

    return () => clearTimeout(timer)
  }, [fetchAllowances])

  // Also fetch immediately when branches and jobs are available
  useEffect(() => {
    if (branches.length > 0 || jobs.length > 0) {
      fetchAllowances()
    }
  }, [branches, jobs, fetchAllowances])

  const handleRefresh = () => {
    fetchAllowances()
    toast.success('Data refreshed successfully')
  }

  const openModal = (modalName, allowance = null) => {
    setSelectedAllowance(allowance)
    setModals(prev => ({ ...prev, [modalName]: true }))
  }

  const closeModal = (modalName) => {
    setModals(prev => ({ ...prev, [modalName]: false }))
    setSelectedAllowance(null)
  }

  const handleDelete = async () => {
    if (!selectedAllowance) return
    
    try {
      const response = await employeeAllowanceService.deleteEmployeeAllowance(selectedAllowance._id)
      if (response.success) {
        toast.success(response.message || 'Employee allowance deleted successfully')
        fetchAllowances()
        closeModal('delete')
      } else {
        toast.error(response.message || 'Failed to delete employee allowance')
      }
    } catch (error) {
      console.error('Error deleting allowance:', error)
      toast.error('Failed to delete employee allowance')
    }
  }

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const clearFilters = () => {
    setFilters({
      branch: '',
      job: '',
      search: ''
    })
  }

  const filteredAllowances = allowances.filter(allowance => {
    const searchMatch = !filters.search || 
      allowance.employee?.fullName?.toLowerCase().includes(filters.search.toLowerCase()) ||
      allowance.employee?.branchName?.toLowerCase().includes(filters.search.toLowerCase()) ||
      allowance.employee?.jobTitle?.toLowerCase().includes(filters.search.toLowerCase())

    const branchMatch = !filters.branch || allowance.employee?.branch === filters.branch
    const jobMatch = !filters.job || allowance.employee?.job === filters.job

    return searchMatch && branchMatch && jobMatch
  })

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toFixed(2)
  }

  const shouldShowClearButton = filters.branch || filters.job || filters.search

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Employee Allowances</h1>        </div>
        <div className="flex gap-3">
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:bg-gray-100"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => openModal('create')}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create New
          </button>
        </div>
      </div>

      {/* Filter Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Filter className="h-5 w-5 mr-2" />
            Filter
          </h3>
          {shouldShowClearButton && (
            <button
              onClick={clearFilters}
              className="px-4 py-2 border border-orange-400 text-orange-500 rounded-lg hover:bg-orange-50 transition-colors text-sm font-medium"
            >
              Clear Filters
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search employees..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          </div>

          {/* Branch Filter */}
          <div className="relative">
            <select
              value={filters.branch}
              onChange={(e) => handleFilterChange('branch', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 appearance-none bg-white"
            >
              <option value="">All Branches</option>
              {branches.map(branch => (
                <option key={branch._id} value={branch._id}>
                  {branch.name}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
          </div>

          {/* Job Filter */}
          <div className="relative">
            <select
              value={filters.job}
              onChange={(e) => handleFilterChange('job', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 appearance-none bg-white"
            >
              <option value="">All Jobs</option>
              {jobs.map(job => (
                <option key={job._id} value={job._id}>
                  {job.name || job.title}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Allowances Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">All Employee Allowances</h2>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
            <span className="ml-2 text-gray-600">Loading allowances...</span>
          </div>
        ) : filteredAllowances.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No employee allowances found</p>
            <p className="text-gray-400 text-sm mt-1">
              {allowances.length === 0 ? 'Create your first employee allowance to get started' : 'Try adjusting your filters'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employee name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Branch
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Job
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    House
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Transport
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Foods
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Health
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAllowances.map((allowance) => (
                  <tr key={allowance._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {allowance.employee?.fullName || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {allowance.employee?.branchName || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {allowance.employee?.jobTitle || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatCurrency(allowance.houseAllowance)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatCurrency(allowance.transportAllowance)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatCurrency(allowance.foodAllowance)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatCurrency(allowance.healthAllowance)}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => openModal('view', allowance)}
                          className="text-gray-400 hover:text-gray-600 p-1"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openModal('edit', allowance)}
                          className="text-gray-400 hover:text-gray-600 p-1"
                          title="Edit Allowance"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openModal('delete', allowance)}
                          className="text-gray-400 hover:text-gray-600 p-1"
                          title="Delete Allowance"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      <CreateEmployeeAllowanceModal
        isOpen={modals.create}
        onClose={() => closeModal('create')}
        onSuccess={fetchAllowances}
      />

      <EditEmployeeAllowanceModal
        isOpen={modals.edit}
        onClose={() => closeModal('edit')}
        allowance={selectedAllowance}
        onSuccess={fetchAllowances}
      />

      <ViewEmployeeAllowanceModal
        isOpen={modals.view}
        onClose={() => closeModal('view')}
        allowance={selectedAllowance}
      />

      {/* Delete Confirmation Modal */}
      {modals.delete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Delete</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete allowances for "{selectedAllowance?.employee?.fullName}"? 
                This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => closeModal('delete')}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default EmployeeAllowances
